<!--
Copyright 2020 ODK Central Developers
See the NOTICE file at the top-level directory of this distribution and at
https://github.com/getodk/central-frontend/blob/master/NOTICE.

This file is part of ODK Central. It is subject to the license terms in
the LICENSE file found in the top-level directory of this distribution and at
https://www.apache.org/licenses/LICENSE-2.0. No part of ODK Central,
including this file, may be copied, modified, propagated, or distributed
except according to the terms contained in the LICENSE file.
-->
<template>
  <modal id="form-draft-abandon" :state="state" :hideable="!awaitingResponse"
    backdrop @hide="$emit('hide')">
    <template #title>{{ title }}</template>
    <template #body>
      <div class="modal-introduction">
        <template v-if="form.publishedAt != null">
          <p>{{ $t('introduction.abandon[0]') }}</p>
          <p>{{ $t('introduction.abandon[1]') }}</p>
        </template>
        <template v-else>
          <p>{{ $t('introduction.deleteForm[0]') }}</p>
        </template>
        <p>{{ $t('common.areYouSure') }}</p>
      </div>
      <div class="modal-actions">
        <button type="button" class="btn btn-danger"
          :disabled="awaitingResponse" @click="abandon">
          {{ $t('action.abandon') }} <spinner :state="awaitingResponse"/>
        </button>
        <button type="button" class="btn btn-link" :disabled="awaitingResponse"
          @click="$emit('hide')">
          {{ $t('action.cancel') }}
        </button>
      </div>
    </template>
  </modal>
</template>

<script>
import Modal from '../modal.vue';
import Spinner from '../spinner.vue';
import request from '../../mixins/request';
import { apiPaths } from '../../util/request';
import { noop } from '../../util/util';
import { requestData } from '../../store/modules/request';

export default {
  name: 'FormDraftAbandon',
  components: { Modal, Spinner },
  mixins: [request()],
  props: {
    state: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      awaitingResponse: false
    };
  },
  computed: {
    // The component assumes that this data will exist when the component is
    // created.
    ...requestData(['form']),
    title() {
      return this.form.publishedAt != null
        ? this.$t('title.abandon')
        : this.$t('title.deleteForm');
    }
  },
  methods: {
    abandon() {
      this.request({
        method: 'DELETE',
        url: this.form.publishedAt != null
          ? apiPaths.formDraft(this.form.projectId, this.form.xmlFormId)
          : apiPaths.form(this.form.projectId, this.form.xmlFormId)
      })
        .then(() => {
          // project.forms and project.lastSubmission may now be out-of-date. If
          // the user navigates to ProjectOverview, project.forms should be
          // updated. project.lastSubmission is not used within ProjectShow.
          this.$emit('success', this.form);
        })
        .catch(noop);
    }
  }
};
</script>

<i18n lang="json5">
{
  "en": {
    // This is the title at the top of a pop-up.
    "title": {
      "abandon": "Abandon Draft",
      "deleteForm": "Abandon Draft and Delete Form"
    },
    "introduction": {
      "abandon": [
        "You are about to permanently delete the Draft version of this Form. This means that the draft Form definition, any draft Media Files you have uploaded, and all test Submissions will be removed.",
        "Your published Form definition, its Media Files, and Submissions will not be affected."
      ],
      "deleteForm": [
        "You are about to permanently delete this draft Form definition, along with any draft Media Files you have uploaded, and all test Submissions. Because you have not yet published it, this Form will be entirely deleted."
      ]
    },
    "action": {
      "abandon": "Abandon"
    }
  }
}
</i18n>

<!-- Autogenerated by destructure.js -->
<i18n>
{
  "cs": {
    "title": {
      "abandon": "Opustit koncept",
      "deleteForm": "Opustit koncept a smazat formulář"
    },
    "introduction": {
      "abandon": [
        "Chystáte se trvale smazat pracovní verzi tohoto formuláře. To znamená, že definice konceptu formuláře, všechny koncepty mediálních souborů, které jste nahráli, a všechna testovací odeslání budou odstraněna.",
        "Vaše publikovaná definice formuláře, její mediální soubory a odeslání nebudou ovlivněny."
      ],
      "deleteForm": [
        "Chystáte se trvale smazat tuto definici konceptu formuláře, spolu se všemi koncepty mediálních souborů, které jste nahráli, a všechny testovací příspěvky. Protože jste jej ještě nezveřejnili, bude tento formulář zcela smazán."
      ]
    },
    "action": {
      "abandon": "Opustit"
    }
  },
  "de": {
    "title": {
      "abandon": "Entwurf verwerfen",
      "deleteForm": "Entwurf verwerfen und Formular löschen"
    },
    "introduction": {
      "abandon": [
        "Sie sind dabei, die Entwurfsversion dieses Formulars zu löschen. Das bedeutet, dass das Formular, hochgeladene dazugehörige Mediendateien und alle Test-Übermittlungen entfernt werden.",
        "Ihre veröffentlichten Forumulardefinitionen, ihre Mediendateien und Übermittlungen werden nicht angetastet."
      ],
      "deleteForm": [
        "Sie sind dabei, diese Formulardefinition, die dazugehörenden hochgeladenen Mediendaten und alle Testübermittlungen zu löschen. Weil Sie das Formular noch nicht veröffentlicht haben, wird es vollständig gelöscht."
      ]
    },
    "action": {
      "abandon": "Abbrechen"
    }
  },
  "es": {
    "title": {
      "abandon": "Abandonar borrador",
      "deleteForm": "Abandonar borrador y borrar formulario"
    },
    "introduction": {
      "abandon": [
        "Está a punto de borrar permanentemente la versión borrador de este formulario. Esto significa que se borrará el borrador de la definición del formulario, cualquier borrador de archivos multimedia que haya cargado y todos los envíos de prueba.",
        "Su definición de formulario publicada, sus archivos multimedia y envíos no se verán afectados."
      ],
      "deleteForm": [
        "Está a punto de borrar permanentemente este borrador de definición de formulario, junto con cualquier borrador de archivos multimedia que haya cargado y todos los envíos de prueba. Como aún no lo ha publicado, este formulario se eliminará por completo."
      ]
    },
    "action": {
      "abandon": "Abandonar"
    }
  },
  "fr": {
    "title": {
      "abandon": "Abandonner l'ébauche",
      "deleteForm": "Abandonner l'ébauche et supprimer le formulaire"
    },
    "introduction": {
      "abandon": [
        "Vous êtes sur le point de supprimer définitivement la version d'ébauche de ce formulaire. Sa définition, ses fichiers multimédias liés et toutes ses soumissions de test seront supprimés.",
        "Votre définition de formulaire publiée, ses fichiers média et ses données soumises ne seront pas affectés."
      ],
      "deleteForm": [
        "Vous êtes sur le point de supprimer définitivement la version d'ébauche de ce formulaire, ainsi que tous ses fichiers multimédias liés et toutes ses soumissions de test. Parce que ce formulaire n'a pas encore de version publiée, il sera entièrement supprimé."
      ]
    },
    "action": {
      "abandon": "Abandonner"
    }
  }
}
</i18n>
